import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import posthog from "@/lib/posthog";
import { trackEvent } from "@/lib/analytics";
import { AppFooter } from "@/components/app-footer";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useUpload } from "@/hooks/use-upload";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { VideoTimeline } from "@/components/video-timeline";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { AppHeader } from "@/components/app-header";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import {
  FlipHorizontal,
  Play,
  Pause,
  X,
  ChevronLeft,
  ChevronRight,
  Maximize,
  Minimize,
  Gauge,
  Share2,
  Check,
  Repeat,
  Repeat1,
  Bookmark,
  Lock,
  Loader2,
  StickyNote,
  ChevronDown,
  ChevronUp,
  MoreHorizontal,
  Pencil,
  Trash2,
  Plus,
} from "lucide-react";
import { Link, useLocation } from "wouter";
import { UpgradeModal } from "@/components/upgrade-modal";
import { CloudflareVideoPlayer } from "@/components/cloudflare-video-player";
import { HeroSection } from "@/components/hero-section";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerClose } from "@/components/ui/drawer";

declare global {
  interface Window {
    YT: {
      Player: new (
        elementId: string,
        config: {
          videoId: string;
          width?: string | number;
          height?: string | number;
          playerVars?: Record<string, number | string>;
          events?: {
            onReady?: (event: { target: YTPlayer }) => void;
            onStateChange?: (event: { data: number; target: YTPlayer }) => void;
          };
        }
      ) => YTPlayer;
      PlayerState: {
        PLAYING: number;
        PAUSED: number;
        ENDED: number;
      };
    };
    onYouTubeIframeAPIReady?: () => void;
  }
}

interface YTPlayer {
  playVideo: () => void;
  pauseVideo: () => void;
  seekTo: (seconds: number, allowSeekAhead: boolean) => void;
  getCurrentTime: () => number;
  getDuration: () => number;
  getPlayerState: () => number;
  setPlaybackRate: (rate: number) => void;
  destroy: () => void;
}

// Validate YouTube video ID format (alphanumeric, hyphens, underscores, 11 chars typical)
function isValidYouTubeId(id: string): boolean {
  // YouTube IDs are typically 11 characters, alphanumeric with hyphens and underscores
  // Being slightly lenient on length (8-12) to handle edge cases
  return /^[a-zA-Z0-9_-]{8,12}$/.test(id);
}

function extractYouTubeId(url: string): string | null {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
    /youtube\.com\/shorts\/([^&\n?#]+)/,
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && isValidYouTubeId(match[1])) {
      return match[1];
    }
  }
  return null;
}

function loadYouTubeAPI(): Promise<void> {
  return new Promise((resolve) => {
    if (window.YT && window.YT.Player) {
      resolve();
      return;
    }
    const existingScript = document.getElementById("youtube-iframe-api");
    if (existingScript) {
      window.onYouTubeIframeAPIReady = () => resolve();
      return;
    }
    const script = document.createElement("script");
    script.id = "youtube-iframe-api";
    script.src = "https://www.youtube.com/iframe_api";
    window.onYouTubeIframeAPIReady = () => resolve();
    document.body.appendChild(script);
  });
}

function clampPan(px: number, py: number, zoom: number, containerW: number, containerH: number) {
  if (zoom <= 1) return { x: 0, y: 0 };
  const maxX = (containerW * (zoom - 1)) / 2;
  const maxY = (containerH * (zoom - 1)) / 2;
  return { x: Math.max(-maxX, Math.min(maxX, px)), y: Math.max(-maxY, Math.min(maxY, py)) };
}

export default function Home() {
  useEffect(() => { document.title = "DanceMirror - Mirror YouTube Dance Videos for Practice | Free Online Tool"; }, []);
  const auth = useAuth();
  const [, setLocation] = useLocation();
  const [cameFromLibrary, setCameFromLibrary] = useState(false);
  const [videoUrl, setVideoUrl] = useState("");
  const [youtubeId, setYoutubeId] = useState<string | null>(null);
  const [uploadedVideo, setUploadedVideo] = useState<string | null>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadedVideoId, setUploadedVideoId] = useState<number | null>(null);
  const [cloudflareStreamId, setCloudflareStreamId] = useState<string | null>(null);
  const [cloudflareHlsUrl, setCloudflareHlsUrl] = useState<string | null>(null);
  const [cfProcessing, setCfProcessing] = useState(false);
  const [cfReconnecting, setCfReconnecting] = useState(false);
  const [isSavingUpload, setIsSavingUpload] = useState(false);
  const [isMirrored, setIsMirrored] = useState(true);
  const [showLeftRight, setShowLeftRight] = useState(false);
  const [seekIndicator, setSeekIndicator] = useState<{ amount: number; id: number; side?: 'left' | 'right' } | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [ytReady, setYtReady] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [isPortrait, setIsPortrait] = useState(false);
  const [showFullscreenControls, setShowFullscreenControls] = useState(true);
  const [linkCopied, setLinkCopied] = useState(false);
  const practiceStartedRef = useRef(false);
  const [loopPointA, setLoopPointA] = useState<number | null>(null);
  const [loopPointB, setLoopPointB] = useState<number | null>(null);
  const [loopEnabled, setLoopEnabled] = useState(false);
  const [videoLoop, setVideoLoop] = useState(false);
  const videoLoopRef = useRef(false);
  const [bufferedPercent, setBufferedPercent] = useState(0);
  const [isBuffering, setIsBuffering] = useState(false);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [upgradeModalMode, setUpgradeModalMode] = useState<"login" | "upgrade">("login");
  const [upgradeFeatureName, setUpgradeFeatureName] = useState("saving videos");
  const [savedVideoId, setSavedVideoId] = useState<number | null>(null);
  const [notesOpen, setNotesOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [notesText, setNotesText] = useState("");
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const [isGrabbing, setIsGrabbing] = useState(false);
  const [showZoomBadge, setShowZoomBadge] = useState(false);
  const [notesSaving, setNotesSaving] = useState(false);
  const [notesDirty, setNotesDirty] = useState(false);
  const notesSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const notesVideoIdRef = useRef<number | null>(null);
  const [pendingAnnotationTime, setPendingAnnotationTime] = useState<number | null>(null);
  const [newAnnotationText, setNewAnnotationText] = useState("");
  const [editingAnnotationId, setEditingAnnotationId] = useState<number | null>(null);
  const [editingAnnotationText, setEditingAnnotationText] = useState("");

  const speedOptions = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 2];

  const uploadErrorRef = useRef<Error | null>(null);
  const { uploadFile, isUploading, progress: uploadProgress } = useUpload({
    onError: (err) => {
      uploadErrorRef.current = err;
    },
  });

  const FREE_UPLOAD_LIMIT = 1;

  const { data: savedVideos } = useQuery<any[]>({
    queryKey: ["/api/videos"],
    enabled: auth.isAuthenticated,
  });

  const { data: savedVideoData } = useQuery<any>({
    queryKey: ["/api/videos", savedVideoId],
    enabled: auth.isAuthenticated && savedVideoId !== null,
  });

  const isPaid = ["monthly", "yearly", "lifetime"].includes(auth.user?.subscriptionTier ?? "");

  const { data: annotationsData, refetch: refetchAnnotations } = useQuery<{ annotations: any[] }>({
    queryKey: ["/api/videos", savedVideoId, "annotations"],
    queryFn: () => apiRequest("GET", `/api/videos/${savedVideoId}/annotations`).then((r) => r.json()),
    enabled: auth.isAuthenticated && isPaid && savedVideoId !== null,
  });
  const annotations: any[] = annotationsData?.annotations ?? [];
  const moreControlsActive = videoLoop || (loopEnabled && loopPointA !== null && loopPointB !== null);

  const { data: storageData } = useQuery<{
    usedSeconds: number;
    warnThresholdSeconds: number;
    limitSeconds: number;
    isNearLimit: boolean;
    isAtLimit: boolean;
  }>({
    queryKey: ["/api/videos/storage-usage"],
    enabled: auth.isAuthenticated && isPaid,
  });

  const uploadedVideoCount = savedVideos?.filter((v: any) => v.videoType === "upload").length ?? 0;
  const isOverLimit = isPaid ? (storageData?.isAtLimit ?? false) : (uploadedVideoCount >= FREE_UPLOAD_LIMIT);
  const isNearLimit = isPaid ? (storageData?.isNearLimit ?? false) : false;

  const saveMutation = useMutation({
    mutationFn: async () => {
      const settings = {
        isMirrored,
        showLeftRight,
        playbackSpeed,
        loopStart: loopPointA,
        loopEnd: loopPointB,
      };

      let res: Response;
      if (youtubeId) {
        let videoTitle = `YouTube: ${youtubeId}`;
        try {
          const oembedRes = await fetch(`https://noembed.com/embed?url=https://www.youtube.com/watch?v=${youtubeId}`);
          if (oembedRes.ok) {
            const oembedData = await oembedRes.json();
            if (oembedData.title) videoTitle = oembedData.title;
          }
        } catch (e) {}
        res = await apiRequest("POST", "/api/videos/save", {
          videoType: "youtube",
          youtubeId,
          thumbnailUrl: `https://img.youtube.com/vi/${youtubeId}/mqdefault.jpg`,
          title: videoTitle,
          settings,
        });
      } else if (uploadedFile) {
        setIsSavingUpload(true);
        uploadErrorRef.current = null;
        const uploadResult = await uploadFile(uploadedFile);
        if (!uploadResult) throw uploadErrorRef.current ?? new Error("Upload failed");
        res = await apiRequest("POST", "/api/videos/save", {
          videoType: "upload",
          cloudflareStreamId: uploadResult.cloudflareStreamId,
          fileName: uploadedFile.name,
          title: uploadedFile.name,
          settings,
        });
        setIsSavingUpload(false);
      } else {
        return;
      }
      const saved = await res.json();
      return saved;
    },
    onSuccess: (data: any) => {
      setIsSavingUpload(false);
      queryClient.invalidateQueries({ queryKey: ["/api/videos"] });
      queryClient.invalidateQueries({ queryKey: ["/api/videos/storage-usage"] });
      if (data?.id) {
        setSavedVideoId(data.id);
        notesVideoIdRef.current = data.id;
      }
      posthog.capture('video_saved', { video_type: data?.videoType ?? 'unknown' });
      trackEvent("practice_video_saved", {
        source_type: data?.videoType === "youtube" ? "youtube" : "upload",
      });
      toast({ title: "Video saved to your library" });
      if (data?.storageWarning) {
        toast({
          title: "Approaching storage limit",
          description: "You're nearing your 120-minute storage limit. Visit your library to remove old uploads.",
        });
      }
    },
    onError: (error: any) => {
      setIsSavingUpload(false);
      console.error("Save mutation error:", error);
      trackEvent("practice_video_save_failed", {
        source_type: youtubeId ? "youtube" : "upload",
      });
      if (error?.message?.includes("120-minute storage limit")) {
        toast({
          title: "Storage limit reached",
          description: "You've used your 120 minutes of storage. Delete uploads from your library to free up space.",
          variant: "destructive",
        });
        queryClient.invalidateQueries({ queryKey: ["/api/videos/storage-usage"] });
      } else if (error?.message?.includes("Free accounts can save")) {
        setUpgradeFeatureName("saving more uploads");
        setUpgradeModalMode("upgrade");
        setUpgradeModalOpen(true);
        queryClient.invalidateQueries({ queryKey: ["/api/videos"] });
      } else {
        toast({ title: "Failed to save video", description: error?.message || "Please try again", variant: "destructive" });
      }
    },
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const videoId = params.get('v');
    const uploadedId = params.get('uploaded');
    const mirrorParam = params.get('mirror');
    const mirror = mirrorParam === '0' ? false : true;
    const lr = params.get('lr') === '1';
    const speed = params.get('speed');
    const loopA = params.get('loopA');
    const loopB = params.get('loopB');
    
    const from = params.get('from');
    if (from === 'library') setCameFromLibrary(true);

    const savedIdParam = params.get('savedId');
    if (savedIdParam) {
      const sid = parseInt(savedIdParam, 10);
      if (!isNaN(sid)) setSavedVideoId(sid);
    }

    if (videoId && isValidYouTubeId(videoId)) {
      setTimeout(() => window.scrollTo(0, 0), 0);
      setYoutubeId(videoId);
      setIsMirrored(mirror);
      setShowLeftRight(lr);
    } else if (uploadedId) {
      setTimeout(() => window.scrollTo(0, 0), 0);
      const vid = parseInt(uploadedId, 10);
      if (!isNaN(vid)) {
        setUploadedVideoId(vid);
        setSavedVideoId(vid);
        setCurrentTime(0);
        setDuration(0);
        setIsPlaying(false);
        setIsMirrored(mirror);
        setShowLeftRight(lr);
        fetch(`/api/videos/${vid}/stream`, { credentials: 'same-origin' })
          .then(r => {
            if (!r.ok) throw new Error(`Failed to load video (${r.status})`);
            return r.json();
          })
          .then(data => {
            if (data.type === "cloudflare" && data.streamId) {
              if (!data.hlsUrl) {
                setCfProcessing(true);
              }
              setCloudflareStreamId(data.streamId);
              if (data.hlsUrl) {
                setCloudflareHlsUrl(data.hlsUrl);
              }
            } else if (data.url) {
              setUploadedVideo(data.url);
            } else {
              throw new Error("No video stream found");
            }
          })
          .catch((err) => {
            console.error("Error loading uploaded video:", err);
            setUploadedVideoId(null);
            setCloudflareStreamId(null);
            setCloudflareHlsUrl(null);
            toast({
              title: "Couldn't load video",
              description: "The video file may have been removed. Try uploading it again.",
              variant: "destructive",
            });
          });
      }
    }
    if (speed) {
      const s = parseFloat(speed);
      if (!isNaN(s) && s > 0 && s <= 2) setPlaybackSpeed(s);
    }
    if (loopA && loopB) {
      const a = parseFloat(loopA);
      const b = parseFloat(loopB);
      if (!isNaN(a) && !isNaN(b) && b > a) {
        setLoopPointA(a);
        setLoopPointB(b);
        setLoopEnabled(true);
      }
    }
  }, []);

  useEffect(() => {
    if (youtubeId) {
      const params = new URLSearchParams();
      params.set('v', youtubeId);
      if (savedVideoId !== null) params.set('savedId', String(savedVideoId));
      if (!isMirrored) params.set('mirror', '0');
      if (showLeftRight) params.set('lr', '1');
      if (playbackSpeed !== 1) params.set('speed', String(playbackSpeed));
      if (loopPointA != null && loopPointB != null) {
        params.set('loopA', String(loopPointA));
        params.set('loopB', String(loopPointB));
      }
      
      const newUrl = `${window.location.pathname}?${params.toString()}`;
      window.history.replaceState({}, '', newUrl);
    } else if (uploadedVideoId) {
      const params = new URLSearchParams();
      params.set('uploaded', String(uploadedVideoId));
      if (!isMirrored) params.set('mirror', '0');
      if (showLeftRight) params.set('lr', '1');
      if (playbackSpeed !== 1) params.set('speed', String(playbackSpeed));
      if (loopPointA != null && loopPointB != null) {
        params.set('loopA', String(loopPointA));
        params.set('loopB', String(loopPointB));
      }
      const newUrl = `${window.location.pathname}?${params.toString()}`;
      window.history.replaceState({}, '', newUrl);
    } else {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [youtubeId, uploadedVideoId, savedVideoId, isMirrored, showLeftRight, playbackSpeed, loopPointA, loopPointB]);

  const videoRef = useRef<HTMLVideoElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const ytPlayerRef = useRef<YTPlayer | null>(null);
  const ytContainerRef = useRef<HTMLDivElement>(null);
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const timeUpdateInterval = useRef<number | null>(null);
  const controlsTimeoutRef = useRef<number | null>(null);
  const cfVideoRef = useRef<HTMLVideoElement | null>(null);
  const videoAreaRef = useRef<HTMLDivElement>(null);
  const [videoContentRect, setVideoContentRect] = useState<{ top: number; left: number; right: number; bottom: number } | null>(null);
  const [containerDims, setContainerDims] = useState<{ w: number; h: number } | null>(null);
  const isDraggingRef = useRef(false);
  const isPanningRef = useRef(false);
  const isMirroredRef = useRef(true);
  const dragStartXRef = useRef(0);
  const dragStartYRef = useRef(0);
  const panAtDragStartXRef = useRef(0);
  const panAtDragStartYRef = useRef(0);
  const zoomLevelRef = useRef(1);
  const panXRef = useRef(0);
  const panYRef = useRef(0);
  const isPinchingRef = useRef(false);
  const pinchStartDistRef = useRef(0);
  const pinchStartZoomRef = useRef(1);
  const isFullscreenRef = useRef(false);
  const controlsRef = useRef<HTMLDivElement | null>(null);
  const compensateRef = useRef<() => void>(() => {});
  const zoomBadgeTimerRef = useRef<number | null>(null);
  const lastTapTimeRef = useRef<number>(0);
  const singleTapTimerRef = useRef<number | null>(null);
  const { toast } = useToast();
  const [videoAreaEl, setVideoAreaEl] = useState<HTMLDivElement | null>(null);
  const videoAreaCallbackRef = useCallback((el: HTMLDivElement | null) => {
    videoAreaRef.current = el;
    setVideoAreaEl(el);
  }, []);

  useEffect(() => {
    if (savedVideoData && savedVideoId !== null) {
      notesVideoIdRef.current = savedVideoId;
      if (!notesDirty) {
        setNotesText(savedVideoData.notes || "");
      }
    }
  }, [savedVideoData, savedVideoId]);

  const saveNotes = useCallback(async (videoId: number, text: string) => {
    if (notesVideoIdRef.current !== videoId) return;
    setNotesSaving(true);
    try {
      await apiRequest("PATCH", `/api/videos/${videoId}`, { notes: text || null });
      if (notesVideoIdRef.current === videoId) {
        setNotesDirty(false);
      }
      queryClient.invalidateQueries({ queryKey: ["/api/videos"] });
    } catch {
      toast({ title: "Failed to save notes", variant: "destructive" });
    } finally {
      if (notesVideoIdRef.current === videoId) {
        setNotesSaving(false);
      }
    }
  }, [toast]);

  const handleNotesChange = useCallback((videoId: number, text: string) => {
    setNotesText(text);
    setNotesDirty(true);
    if (notesSaveTimerRef.current) {
      clearTimeout(notesSaveTimerRef.current);
    }
    notesSaveTimerRef.current = setTimeout(() => {
      saveNotes(videoId, text);
    }, 1000);
  }, [saveNotes]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("scrollTo") === "get-started") {
      const checkAndScroll = () => {
        const el = document.querySelector<HTMLInputElement>('[data-testid="input-youtube-url"]');
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          el.focus();
          // Clean up the URL
          const newParams = new URLSearchParams(window.location.search);
          newParams.delete("scrollTo");
          const newSearch = newParams.toString();
          const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : "");
          window.history.replaceState({}, "", newUrl);
        } else {
          setTimeout(checkAndScroll, 100);
        }
      };
      checkAndScroll();
    }
  }, []);

  const hasVideo = youtubeId || uploadedVideo || uploadedVideoId || cloudflareStreamId;

  const trackPracticeStart = useCallback((sourceType: "youtube" | "upload") => {
    if (practiceStartedRef.current) return;
    practiceStartedRef.current = true;
    trackEvent("practice_started", {
      surface: "home",
      source_type: sourceType,
    });
  }, []);

  useEffect(() => {
    practiceStartedRef.current = false;
  }, [youtubeId, uploadedVideo, uploadedVideoId, cloudflareStreamId]);

  const handleShare = useCallback(() => {
    if (!youtubeId) return;

    const params = new URLSearchParams();
    params.set('v', youtubeId);
    if (!isMirrored) params.set('mirror', '0');
    if (showLeftRight) params.set('lr', '1');

    const shareUrl = `${window.location.origin}${window.location.pathname}?${params.toString()}`;
    navigator.clipboard.writeText(shareUrl).then(() => {
      posthog.capture('share_link_copied', { video_type: 'youtube' });
      trackEvent("share_link_copied", {
        source_type: "youtube",
        location: "practice_player",
      });
      setLinkCopied(true);
      toast({
        title: "Link copied!",
        description: "Share this link with others to show them the mirrored video",
      });
      setTimeout(() => setLinkCopied(false), 2000);
    }).catch(() => {
      toast({
        title: "Failed to copy",
        description: "Please copy the URL from the address bar instead",
        variant: "destructive",
      });
    });
  }, [youtubeId, isMirrored, showLeftRight, toast]);


  const handleUrlSubmit = useCallback(() => {
    const id = extractYouTubeId(videoUrl);
    if (id) {
      if (uploadedVideo && uploadedVideo.startsWith("blob:")) {
        URL.revokeObjectURL(uploadedVideo);
      }
      setYoutubeId(id);
      posthog.capture('video_loaded', { video_type: 'youtube', youtube_id: id });
      trackEvent("practice_video_loaded", {
        source_type: "youtube",
        entry_point: "url",
      });
      setUploadedVideo(null);
      setCloudflareStreamId(null);
      setCloudflareHlsUrl(null);
      setYtReady(false);
      setCurrentTime(0);
      setDuration(0);
      setIsPlaying(false);
      setTimeout(() => window.scrollTo(0, 0), 0);
    } else if (videoUrl.trim()) {
      toast({
        title: "Invalid YouTube URL",
        description: "Please enter a valid YouTube video link (youtube.com/watch?v=... or youtu.be/...)",
        variant: "destructive",
      });
    }
  }, [videoUrl, uploadedVideo, toast]);

  const MAX_FILE_SIZE_MB = auth.isAuthenticated ? 4096 : 500;
  const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024;

  const loadVideoFile = useCallback((file: File) => {
    if (uploadedVideo && uploadedVideo.startsWith("blob:")) {
      URL.revokeObjectURL(uploadedVideo);
    }
    const url = URL.createObjectURL(file);
    setUploadedVideo(url);
    setUploadedFile(file);
    setCloudflareStreamId(null);
    setCloudflareHlsUrl(null);
    setYoutubeId(null);
    setVideoUrl("");
    posthog.capture('video_loaded', { video_type: 'file', file_name: file.name, file_size_mb: +(file.size / 1024 / 1024).toFixed(1) });
    trackEvent("practice_video_loaded", {
      source_type: "upload",
      entry_point: "file_picker",
    });
    setTimeout(() => window.scrollTo(0, 0), 0);
  }, [uploadedVideo]);

  const handleFileUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith("video/")) {
      if (file.size > MAX_FILE_SIZE) {
        toast({
          title: "File too large",
          description: auth.isAuthenticated
            ? `File is ${(file.size / (1024 * 1024 * 1024)).toFixed(1)} GB. Maximum upload size is 4 GB.`
            : `File is ${(file.size / (1024 * 1024)).toFixed(0)} MB. Sign in to upload up to 4 GB, or keep it under 500 MB.`,
          variant: "destructive",
        });
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }
      loadVideoFile(file);
    }
  }, [loadVideoFile, toast]);

  const handleClearVideo = useCallback(() => {
    if (uploadedVideo && uploadedVideo.startsWith("blob:")) {
      URL.revokeObjectURL(uploadedVideo);
    }
    if (ytPlayerRef.current) {
      ytPlayerRef.current.destroy();
      ytPlayerRef.current = null;
    }
    if (timeUpdateInterval.current) {
      clearInterval(timeUpdateInterval.current);
      timeUpdateInterval.current = null;
    }
    setYoutubeId(null);
    setUploadedVideo(null);
    setUploadedFile(null);
    setUploadedVideoId(null);
    setCloudflareStreamId(null);
    setCloudflareHlsUrl(null);
    setCfProcessing(false);
    setVideoUrl("");
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setYtReady(false);
    setPlaybackSpeed(1);
    setLoopPointA(null);
    setLoopPointB(null);
    setLoopEnabled(false);
    setVideoLoop(false);
    setBufferedPercent(0);
    setIsBuffering(false);
    setZoomLevel(1);
    setPanX(0);
    setPanY(0);
    if (notesSaveTimerRef.current) {
      clearTimeout(notesSaveTimerRef.current);
      notesSaveTimerRef.current = null;
    }
    if (notesVideoIdRef.current !== null && notesDirty) {
      saveNotes(notesVideoIdRef.current, notesText);
    }
    setSavedVideoId(null);
    setNotesOpen(false);
    setNotesText("");
    setNotesDirty(false);
    setNotesSaving(false);
    notesVideoIdRef.current = null;
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [uploadedVideo, notesDirty, notesText, saveNotes]);

  const handleBack = useCallback(() => {
    if (cameFromLibrary) setLocation('/library');
    else handleClearVideo();
  }, [cameFromLibrary, handleClearVideo, setLocation]);

  const setPointA = useCallback(() => {
    setLoopPointA(currentTime);
    posthog.capture('ab_loop_point_set', { point: 'a', time: currentTime });
    if (loopPointB !== null && currentTime >= loopPointB) {
      setLoopPointB(null);
      setLoopEnabled(false);
    }
  }, [currentTime, loopPointB]);

  const setPointB = useCallback(() => {
    if (loopPointA !== null && currentTime > loopPointA) {
      setLoopPointB(currentTime);
      setLoopEnabled(true);
      posthog.capture('ab_loop_point_set', { point: 'b', time: currentTime });
    }
  }, [currentTime, loopPointA]);

  const clearLoop = useCallback(() => {
    setLoopPointA(null);
    setLoopPointB(null);
    setLoopEnabled(false);
    posthog.capture('ab_loop_cleared');
  }, []);

  const speedSyncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!cloudflareStreamId) return;
    if (!cloudflareHlsUrl) {
      setCfProcessing(true);
    }
    let mounted = true;
    let pollTimer: ReturnType<typeof setTimeout> | null = null;
    let failCount = 0;

    const checkStatus = async () => {
      try {
        const res = await fetch(`/api/cloudflare/status/${cloudflareStreamId}`, {
          credentials: "same-origin",
        });
        if (!mounted) return;
        if (!res.ok) {
          failCount++;
          if (failCount >= 3) {
            setCfProcessing(false);
            return;
          }
          pollTimer = setTimeout(checkStatus, 3000);
          return;
        }
        const data = await res.json();
        if (data.readyToStream) {
          setCfProcessing(false);
          if (data.playback?.hls && !cloudflareHlsUrl) {
            setCloudflareHlsUrl(data.playback.hls);
          }
        } else {
          setCfProcessing(true);
          pollTimer = setTimeout(checkStatus, 3000);
        }
      } catch {
        failCount++;
        if (mounted && failCount < 3) {
          pollTimer = setTimeout(checkStatus, 5000);
        } else if (mounted) {
          setCfProcessing(false);
        }
      }
    };

    checkStatus();

    return () => {
      mounted = false;
      if (pollTimer) clearTimeout(pollTimer);
    };
  }, [cloudflareStreamId, cloudflareHlsUrl]);


  const changeSpeed = useCallback((speed: number) => {
    setPlaybackSpeed(speed);
    if (videoRef.current) {
      const vid = videoRef.current;
      vid.playbackRate = speed;

      if (uploadedVideo && !vid.paused && vid.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
        if (speedSyncTimerRef.current) clearTimeout(speedSyncTimerRef.current);
        speedSyncTimerRef.current = setTimeout(() => {
          if (videoRef.current && !videoRef.current.paused) {
            const t = videoRef.current.currentTime;
            videoRef.current.pause();
            videoRef.current.currentTime = Math.max(0, t - 0.01);
            videoRef.current.play().catch(() => {});
          }
        }, 50);
      }
    }
    if (cfVideoRef.current) {
      try { cfVideoRef.current.playbackRate = speed; } catch {}
    }
    if (ytPlayerRef.current && ytReady) {
      ytPlayerRef.current.setPlaybackRate(speed);
    }
    posthog.capture('speed_changed', { speed });
    trackEvent("practice_control_used", {
      surface: "home",
      control: "speed",
      value: speed,
    });
  }, [uploadedVideo, ytReady]);

  useEffect(() => {
    return () => {
      if (uploadedVideo && uploadedVideo.startsWith("blob:")) {
        URL.revokeObjectURL(uploadedVideo);
      }
    };
  }, [uploadedVideo]);

  useEffect(() => {
    if (!youtubeId) return;

    let mounted = true;

    const initPlayer = async () => {
      await loadYouTubeAPI();
      if (!mounted || !ytContainerRef.current) return;

      if (ytPlayerRef.current) {
        ytPlayerRef.current.destroy();
      }

      const containerId = "yt-player-container";
      ytContainerRef.current.innerHTML = `<div id="${containerId}" style="width:100%;height:100%;"></div>`;

      ytPlayerRef.current = new window.YT.Player(containerId, {
        videoId: youtubeId,
        width: "100%",
        height: "100%",
        playerVars: {
          modestbranding: 1,
          rel: 0,
          controls: 0,
          disablekb: 1,
          fs: 0,
          iv_load_policy: 3,
          playsinline: 1,
        },
        events: {
          onReady: (event) => {
            if (!mounted) return;
            setYtReady(true);
            const dur = event.target.getDuration();
            setDuration(dur);
            if (playbackSpeed !== 1) {
              event.target.setPlaybackRate(playbackSpeed);
            }
          },
          onStateChange: (event) => {
            if (!mounted) return;
            if (event.data === window.YT.PlayerState.ENDED) {
              event.target.seekTo(0, true);
              setCurrentTime(0);
              if (!videoLoopRef.current) {
                event.target.pauseVideo();
                setIsPlaying(false);
              }
              return;
            }
            const playing = event.data === window.YT.PlayerState.PLAYING;
            setIsPlaying(playing);
            if (playing) {
              trackPracticeStart("youtube");
              setDuration(event.target.getDuration());
            }
          },
        },
      });

      timeUpdateInterval.current = window.setInterval(() => {
        if (ytPlayerRef.current && mounted) {
          try {
            const time = ytPlayerRef.current.getCurrentTime();
            if (typeof time === "number") {
              setCurrentTime(time);
            }
          } catch (e) {
            // Player not ready
          }
        }
      }, 250);
    };

    initPlayer();

    return () => {
      mounted = false;
      if (timeUpdateInterval.current) {
        clearInterval(timeUpdateInterval.current);
        timeUpdateInterval.current = null;
      }
    };
  }, [youtubeId, trackPracticeStart]);

  // A-B Loop logic - checks if we've reached point B and loops back to A
  useEffect(() => {
    if (!loopEnabled || loopPointA === null || loopPointB === null) return;
    
    if (currentTime >= loopPointB) {
      if (cfVideoRef.current && cloudflareStreamId) {
        try { cfVideoRef.current.currentTime = loopPointA; } catch {}
      } else if (uploadedVideo && videoRef.current) {
        videoRef.current.currentTime = loopPointA;
      } else if (youtubeId && ytPlayerRef.current && ytReady) {
        try {
          ytPlayerRef.current.seekTo(loopPointA, true);
        } catch (e) {
          // Player not ready
        }
      }
    }
  }, [currentTime, loopEnabled, loopPointA, loopPointB, uploadedVideo, youtubeId, ytReady]);

  useEffect(() => {
    videoLoopRef.current = videoLoop;
  }, [videoLoop]);

  const handleVideoEnded = useCallback(() => {
    if (videoLoop) {
      const videoEl = cfVideoRef.current || videoRef.current;
      if (videoEl) {
        videoEl.currentTime = 0;
        videoEl.play().catch(() => {});
      }
    } else {
      setIsPlaying(false);
    }
  }, [videoLoop]);

  const updateBufferedPercent = useCallback((videoEl: HTMLVideoElement) => {
    if (videoEl.buffered.length > 0 && videoEl.duration > 0) {
      const end = videoEl.buffered.end(videoEl.buffered.length - 1);
      setBufferedPercent((end / videoEl.duration) * 100);
    }
  }, []);

  const togglePlay = useCallback(() => {
    if (cfVideoRef.current && cloudflareStreamId) {
      try {
        if (isPlaying) {
          cfVideoRef.current.pause();
        } else {
          const playPromise = cfVideoRef.current.play();
          if (playPromise !== undefined) {
            playPromise.catch(() => {});
          }
        }
      } catch (e) {}
      return;
    }
    if (uploadedVideo && videoRef.current) {
      try {
        if (isPlaying) {
          videoRef.current.pause();
        } else {
          const playPromise = videoRef.current.play();
          if (playPromise !== undefined) {
            playPromise.catch(() => {});
          }
        }
      } catch (e) {}
      setIsPlaying(!isPlaying);
    } else if (youtubeId && ytPlayerRef.current && ytReady) {
      try {
        if (isPlaying) {
          ytPlayerRef.current.pauseVideo();
        } else {
          ytPlayerRef.current.playVideo();
        }
      } catch (e) {}
    }
  }, [isPlaying, uploadedVideo, youtubeId, ytReady, cloudflareStreamId]);

  const handleSeek = useCallback((value: number[]) => {
    const time = value[0];
    setCurrentTime(time);
    if (cfVideoRef.current && cloudflareStreamId) {
      try { cfVideoRef.current.currentTime = time; } catch {}
    } else if (uploadedVideo && videoRef.current) {
      videoRef.current.currentTime = time;
    } else if (youtubeId && ytPlayerRef.current && ytReady) {
      try {
        ytPlayerRef.current.seekTo(time, true);
      } catch (e) {
        // Player not ready
      }
    }
  }, [uploadedVideo, youtubeId, ytReady, cloudflareStreamId]);

  const skipTime = useCallback((seconds: number, side?: 'left' | 'right') => {
    if (cfVideoRef.current && cloudflareStreamId) {
      try {
        cfVideoRef.current.currentTime = Math.max(0, Math.min(duration, cfVideoRef.current.currentTime + seconds));
      } catch {}
    } else if (uploadedVideo && videoRef.current) {
      videoRef.current.currentTime = Math.max(0, Math.min(duration, videoRef.current.currentTime + seconds));
    } else if (youtubeId && ytPlayerRef.current && ytReady) {
      try {
        const newTime = Math.max(0, Math.min(duration, currentTime + seconds));
        ytPlayerRef.current.seekTo(newTime, true);
        setCurrentTime(newTime);
      } catch (e) {
        // Player not ready
      }
    }
    setSeekIndicator({ amount: seconds, id: Date.now(), side });
  }, [duration, currentTime, uploadedVideo, youtubeId, ytReady, cloudflareStreamId]);

  const toggleFullscreen = useCallback(() => {
    setMoreOpen(false);
    posthog.capture('fullscreen_toggled', { enabled: !isFullscreen });
    trackEvent("practice_control_used", {
      surface: "home",
      control: "fullscreen",
      enabled: !isFullscreen,
    });
    if (!isFullscreen) {
      const el = videoContainerRef.current as HTMLElement & {
        webkitRequestFullscreen?: () => Promise<void>;
      };
      if (el?.requestFullscreen) {
        el.requestFullscreen().catch(() => {});
      } else if (el?.webkitRequestFullscreen) {
        el.webkitRequestFullscreen();
      }
      setIsFullscreen(true);
    } else {
      const doc = document as Document & {
        webkitExitFullscreen?: () => void;
        webkitFullscreenElement?: Element | null;
      };
      if (doc.fullscreenElement || doc.webkitFullscreenElement) {
        if (doc.exitFullscreen) doc.exitFullscreen().catch(() => {});
        else if (doc.webkitExitFullscreen) doc.webkitExitFullscreen();
      }
      setIsFullscreen(false);
    }
  }, [isFullscreen]);

  const startHideTimer = useCallback(() => {
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    controlsTimeoutRef.current = window.setTimeout(() => {
      setShowFullscreenControls(false);
    }, 3000);
  }, []);

  const handleVideoAreaTap = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (isDraggingRef.current) {
      isDraggingRef.current = false;
      if (singleTapTimerRef.current) clearTimeout(singleTapTimerRef.current);
      lastTapTimeRef.current = 0;
      return;
    }

    const now = Date.now();
    const isDoubleTap = now - lastTapTimeRef.current < 300;

    if (isDoubleTap) {
      if (singleTapTimerRef.current) clearTimeout(singleTapTimerRef.current);
      lastTapTimeRef.current = 0;
      const rect = e.currentTarget.getBoundingClientRect();
      const isRightHalf = e.clientX - rect.left > rect.width / 2;
      skipTime(isRightHalf ? 5 : -5, isRightHalf ? 'right' : 'left');
      if (isFullscreenRef.current) {
        setShowFullscreenControls(true);
        startHideTimer();
      }
    } else {
      lastTapTimeRef.current = now;
      singleTapTimerRef.current = window.setTimeout(() => {
        if (isFullscreenRef.current) {
          togglePlay();
          setShowFullscreenControls(true);
          startHideTimer();
        } else {
          togglePlay();
        }
      }, 300);
    }
  }, [skipTime, startHideTimer, togglePlay]);

  useEffect(() => { zoomLevelRef.current = zoomLevel; }, [zoomLevel]);
  useEffect(() => { panXRef.current = panX; }, [panX]);
  useEffect(() => { panYRef.current = panY; }, [panY]);
  useEffect(() => { isFullscreenRef.current = isFullscreen; }, [isFullscreen]);
  useEffect(() => { isMirroredRef.current = isMirrored; }, [isMirrored]);

  const zoomIn = useCallback(() => {
    setZoomLevel(prev => {
      const next = Math.min(4, prev + 0.5);
      const container = videoAreaRef.current;
      if (container) {
        const c = clampPan(panXRef.current, panYRef.current, next, container.clientWidth, container.clientHeight);
        setPanX(c.x); setPanY(c.y);
      }
      return next;
    });
  }, []);

  const zoomOut = useCallback(() => {
    setZoomLevel(prev => {
      const next = Math.max(1, prev - 0.5);
      if (next <= 1) { setPanX(0); setPanY(0); return 1; }
      const container = videoAreaRef.current;
      if (container) {
        const c = clampPan(panXRef.current, panYRef.current, next, container.clientWidth, container.clientHeight);
        setPanX(c.x); setPanY(c.y);
      }
      return next;
    });
  }, []);

  const handleVideoPanStart = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0 && e.pointerType === "mouse") return;
    if (isPinchingRef.current) return;
    isDraggingRef.current = false;
    dragStartXRef.current = e.clientX;
    dragStartYRef.current = e.clientY;
    panAtDragStartXRef.current = panXRef.current;
    panAtDragStartYRef.current = panYRef.current;
    if (zoomLevelRef.current > 1) {
      isPanningRef.current = true;
      try { (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId); } catch {}
      setIsGrabbing(true);
    }
  }, []);

  const handleVideoPanMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!isPanningRef.current) return;
    if (isPinchingRef.current) return;
    if (zoomLevelRef.current <= 1) return;
    const dx = e.clientX - dragStartXRef.current;
    const dy = e.clientY - dragStartYRef.current;
    if (!isDraggingRef.current && Math.sqrt(dx * dx + dy * dy) < 6) return;
    isDraggingRef.current = true;
    const container = videoAreaRef.current;
    if (!container) return;
    const panDx = isMirroredRef.current ? -dx : dx;
    const c = clampPan(
      panAtDragStartXRef.current + panDx / zoomLevelRef.current,
      panAtDragStartYRef.current + dy / zoomLevelRef.current,
      zoomLevelRef.current, container.clientWidth, container.clientHeight
    );
    setPanX(c.x); setPanY(c.y);
  }, []);

  const handleVideoPanEnd = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    isPanningRef.current = false;
    try { (e.currentTarget as HTMLDivElement).releasePointerCapture(e.pointerId); } catch {}
    setIsGrabbing(false);
  }, []);

  const handleVideoPanCancel = useCallback(() => {
    isPanningRef.current = false;
    isDraggingRef.current = false;
    setIsGrabbing(false);
  }, []);

  // Scroll-wheel / trackpad-pinch zoom
  useEffect(() => {
    const el = videoAreaRef.current;
    if (!el) return;
    const handleWheel = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      if (e.deltaY < 0) zoomIn(); else zoomOut();
    };
    el.addEventListener("wheel", handleWheel, { passive: false });
    return () => el.removeEventListener("wheel", handleWheel);
  }, [videoAreaEl, zoomIn, zoomOut]);

  // Native touch handlers: pinch-to-zoom + prevent browser viewport zoom
  useEffect(() => {
    const el = videoAreaRef.current;
    if (!el) return;

    const handleTouchStart = (e: TouchEvent) => {
      if (e.touches.length >= 2) {
        e.preventDefault();
        const vvCheck = window.visualViewport;
        if (vvCheck && vvCheck.scale > 1.01) return;
        if (!isPinchingRef.current) {
          isPinchingRef.current = true;
          isPanningRef.current = false;
          isDraggingRef.current = false;
          setIsGrabbing(false);
          if (zoomBadgeTimerRef.current) clearTimeout(zoomBadgeTimerRef.current);
          setShowZoomBadge(true);
          const dx = e.touches[0].clientX - e.touches[1].clientX;
          const dy = e.touches[0].clientY - e.touches[1].clientY;
          pinchStartDistRef.current = Math.sqrt(dx * dx + dy * dy);
          pinchStartZoomRef.current = zoomLevelRef.current;
        }
      } else {
        isPinchingRef.current = false;
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length >= 2) {
        e.preventDefault();
        if (!isPinchingRef.current) return;
        const vvCheck = window.visualViewport;
        if (vvCheck && vvCheck.scale > 1.01) return;
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const scale = dist / pinchStartDistRef.current;
        const newZoom = Math.max(1, Math.min(4, pinchStartZoomRef.current * scale));
        setZoomLevel(newZoom);
        const container = videoAreaRef.current;
        if (container) {
          const c = clampPan(panXRef.current, panYRef.current, newZoom, container.clientWidth, container.clientHeight);
          setPanX(c.x);
          setPanY(c.y);
        }
      } else if (isPanningRef.current) {
        e.preventDefault();
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (e.touches.length < 2) {
        isPinchingRef.current = false;
        if (zoomBadgeTimerRef.current) clearTimeout(zoomBadgeTimerRef.current);
        zoomBadgeTimerRef.current = window.setTimeout(() => setShowZoomBadge(false), 1200);
      }
    };

    el.addEventListener("touchstart", handleTouchStart, { passive: false });
    el.addEventListener("touchmove", handleTouchMove, { passive: false });
    el.addEventListener("touchend", handleTouchEnd, { passive: true });
    el.addEventListener("touchcancel", handleTouchEnd, { passive: true });
    return () => {
      el.removeEventListener("touchstart", handleTouchStart);
      el.removeEventListener("touchmove", handleTouchMove);
      el.removeEventListener("touchend", handleTouchEnd);
      el.removeEventListener("touchcancel", handleTouchEnd);
    };
  }, [videoAreaEl]);

  useEffect(() => {
    if (isFullscreen) {
      setShowFullscreenControls(true);
      startHideTimer();
      setTimeout(() => {
        const vv = window.visualViewport;
        if (vv) {
          document.documentElement.style.setProperty('--real-vh', `${vv.height * 0.01}px`);
          document.documentElement.style.setProperty('--vv-top', `${vv.offsetTop}px`);
        } else {
          document.documentElement.style.setProperty('--real-vh', `${window.innerHeight * 0.01}px`);
          document.documentElement.style.setProperty('--vv-top', '0px');
        }
      }, 50);
    } else {
      setShowFullscreenControls(true);
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    }
    compensateRef.current();
    setTimeout(updateVideoContentRect, 100);
  }, [isFullscreen]);

  useEffect(() => {
    return () => {
      if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
      if (zoomBadgeTimerRef.current) clearTimeout(zoomBadgeTimerRef.current);
    };
  }, []);

  // Block iOS browser viewport zoom for the lifetime of this page (capture phase
  // fires before any bubble-phase stopPropagation can silence it).
  useEffect(() => {
    const blockPinch = (e: TouchEvent) => {
      if (e.touches.length >= 2) e.preventDefault();
    };
    document.addEventListener("touchstart", blockPinch, { passive: false, capture: true });
    document.addEventListener("touchmove", blockPinch, { passive: false, capture: true });
    return () => {
      document.removeEventListener("touchstart", blockPinch, { capture: true });
      document.removeEventListener("touchmove", blockPinch, { capture: true });
    };
  }, []);

  // Dynamic viewport meta: add user-scalable=no on mount so iOS 16+ respects it.
  useEffect(() => {
    const metaViewport = document.querySelector('meta[name="viewport"]');
    if (!metaViewport) return;
    const original = metaViewport.getAttribute("content") || "";
    const directives: Record<string, string> = {};
    original.split(",").forEach((part) => {
      const [k, v] = part.trim().split("=");
      if (k) directives[k.trim()] = (v ?? "").trim();
    });
    directives["user-scalable"] = "no";
    directives["minimum-scale"] = "1";
    directives["maximum-scale"] = "1";
    metaViewport.setAttribute(
      "content",
      Object.entries(directives)
        .map(([k, v]) => (v ? `${k}=${v}` : k))
        .join(", ")
    );
    return () => {
      metaViewport.setAttribute("content", original);
    };
  }, []);

  // Visual Viewport compensation: when browser viewport zoom slips through,
  // counter-scale the controls bar to keep it full-width at the visual viewport bottom.
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const compensate = () => {
      const controls = controlsRef.current;
      if (!controls) return;
      const s = vv.scale;
      if (s <= 1.001 || !isFullscreenRef.current) {
        controls.style.transform = "";
        controls.style.transformOrigin = "";
        controls.style.width = "";
        controls.style.left = "";
        controls.style.right = "";
        controls.style.bottom = "";
        return;
      }
      const bottomGap = window.innerHeight - vv.offsetTop - vv.height;
      controls.style.left = `${vv.offsetLeft}px`;
      controls.style.right = "auto";
      controls.style.width = `${window.innerWidth}px`;
      controls.style.bottom = `${bottomGap}px`;
      controls.style.transformOrigin = "bottom left";
      controls.style.transform = `scale(${1 / s})`;
    };
    compensateRef.current = compensate;
    vv.addEventListener("resize", compensate);
    vv.addEventListener("scroll", compensate);
    compensate();
    return () => {
      vv.removeEventListener("resize", compensate);
      vv.removeEventListener("scroll", compensate);
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isFullscreen && !upgradeModalOpen) {
        setIsFullscreen(false);
        return;
      }

      const tag = (e.target as HTMLElement)?.tagName?.toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select") return;
      if (!youtubeId && !uploadedVideo) return;

      if (e.key === " ") {
        e.preventDefault();
        togglePlay();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        skipTime(-5);
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        skipTime(5);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isFullscreen, upgradeModalOpen, youtubeId, uploadedVideo, togglePlay, skipTime]);

  useEffect(() => {
    const checkOrientation = () => {
      setIsPortrait(window.innerHeight > window.innerWidth);
    };
    checkOrientation();
    window.addEventListener("resize", checkOrientation);
    return () => window.removeEventListener("resize", checkOrientation);
  }, []);

  useEffect(() => {
    if (isFullscreen) {
      const scrollY = window.scrollY;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = `-${scrollY}px`;
      return () => {
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.width = '';
        document.body.style.top = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isFullscreen]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      const doc = document as Document & { webkitFullscreenElement?: Element | null };
      const isNativeFullscreen = !!(doc.fullscreenElement || doc.webkitFullscreenElement);
      if (!isNativeFullscreen) {
        setIsFullscreen(false);
      }
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    };
  }, []);

  useEffect(() => {
    const updateViewport = () => {
      const vv = window.visualViewport;
      if (vv) {
        document.documentElement.style.setProperty('--real-vh', `${vv.height * 0.01}px`);
        document.documentElement.style.setProperty('--vv-top', `${vv.offsetTop}px`);
      } else {
        document.documentElement.style.setProperty('--real-vh', `${window.innerHeight * 0.01}px`);
        document.documentElement.style.setProperty('--vv-top', '0px');
      }
    };
    updateViewport();
    const vv = window.visualViewport;
    if (vv) {
      vv.addEventListener('resize', updateViewport);
      vv.addEventListener('scroll', updateViewport); // fires when URL bar shows/hides on iOS
    }
    window.addEventListener('resize', updateViewport);
    const handleOrientationChange = () => setTimeout(updateViewport, 200);
    window.addEventListener('orientationchange', handleOrientationChange);
    return () => {
      if (vv) {
        vv.removeEventListener('resize', updateViewport);
        vv.removeEventListener('scroll', updateViewport);
      }
      window.removeEventListener('resize', updateViewport);
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, []);

  const updateVideoContentRect = useCallback(() => {
    const container = videoAreaRef.current;
    if (!container) { setVideoContentRect(null); return; }

    const containerW = container.clientWidth;
    const containerH = container.clientHeight;

    let videoAspect: number | null = null;

    const videoEl = videoRef.current || cfVideoRef.current;
    if (videoEl && videoEl.videoWidth && videoEl.videoHeight) {
      videoAspect = videoEl.videoWidth / videoEl.videoHeight;
    } else if (youtubeId) {
      videoAspect = 16 / 9;
    }

    if (videoAspect !== null) {
      const containerAspect = containerW / containerH;

      let renderW: number, renderH: number;
      if (videoAspect > containerAspect) {
        renderW = containerW;
        renderH = containerW / videoAspect;
      } else {
        renderH = containerH;
        renderW = containerH * videoAspect;
      }

      const top = (containerH - renderH) / 2;
      const left = (containerW - renderW) / 2;
      setContainerDims({ w: containerW, h: containerH });
      setVideoContentRect({
        top,
        left,
        right: left,
        bottom: top,
      });
    } else {
      setContainerDims(containerW && containerH ? { w: containerW, h: containerH } : null);
      setVideoContentRect(null);
    }
  }, [youtubeId]);

  const visibleVideoRect = useMemo(() => {
    if (!videoContentRect || !containerDims) return null;
    const { w: cW, h: cH } = containerDims;
    const cx = cW / 2, cy = cH / 2;
    const vcLeft = videoContentRect.left;
    const vcTop = videoContentRect.top;
    const vcRight = cW - videoContentRect.right;
    const vcBottom = cH - videoContentRect.bottom;
    const visLeft   = Math.max(0,  cx + zoomLevel * (vcLeft   - cx) + panX);
    const visTop    = Math.max(0,  cy + zoomLevel * (vcTop    - cy) + panY);
    const visRight  = Math.min(cW, cx + zoomLevel * (vcRight  - cx) + panX);
    const visBottom = Math.min(cH, cy + zoomLevel * (vcBottom - cy) + panY);
    return {
      top:     visTop,
      left:    visLeft,
      right:   cW - visRight,
      bottom:  cH - visBottom,
      centerX: (visLeft + visRight) / 2,
    };
  }, [videoContentRect, containerDims, zoomLevel, panX, panY]);

  const cfVideoRefCallback = useCallback((el: HTMLVideoElement | null) => {
    if (cfVideoRef.current) {
      cfVideoRef.current.removeEventListener("loadeddata", updateVideoContentRect);
      cfVideoRef.current.removeEventListener("loadedmetadata", updateVideoContentRect);
    }
    cfVideoRef.current = el;
    if (el) {
      el.addEventListener("loadeddata", updateVideoContentRect);
      el.addEventListener("loadedmetadata", updateVideoContentRect);
    }
  }, [updateVideoContentRect]);

  useEffect(() => {
    const container = videoAreaRef.current;
    if (!container) return;

    const observer = new ResizeObserver(() => updateVideoContentRect());
    observer.observe(container);

    const videoEl = videoRef.current || cfVideoRef.current;
    if (videoEl) {
      videoEl.addEventListener("loadedmetadata", updateVideoContentRect);
      videoEl.addEventListener("resize", updateVideoContentRect);
    }

    return () => {
      observer.disconnect();
      if (videoEl) {
        videoEl.removeEventListener("loadedmetadata", updateVideoContentRect);
        videoEl.removeEventListener("resize", updateVideoContentRect);
      }
    };
  }, [updateVideoContentRect, uploadedVideo, cloudflareStreamId, isFullscreen]);

  useEffect(() => {
    if (window.location.hash) {
      const id = window.location.hash.slice(1);
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, []);

  const formatTime = (seconds: number) => {
    if (!isFinite(seconds) || isNaN(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />

      <main className="max-w-6xl mx-auto px-2 sm:px-4 py-4 sm:py-8">
        {!hasVideo ? (
          <HeroSection
            videoUrl={videoUrl}
            setVideoUrl={setVideoUrl}
            handleUrlSubmit={handleUrlSubmit}
            handleFileUpload={handleFileUpload}
            fileInputRef={fileInputRef}
            isAuthenticated={auth.isAuthenticated}
            onExampleClick={(id, title) => {
              setYoutubeId(id);
              setUploadedVideo(null);
              setCloudflareStreamId(null);
              setCloudflareHlsUrl(null);
              setYtReady(false);
              setCurrentTime(0);
              setDuration(0);
              setIsPlaying(false);
              setTimeout(() => window.scrollTo(0, 0), 0);
            }}
          />
        ) : (
          <div className="space-y-2 sm:space-y-4">
            <div ref={toolbarRef} className="flex items-center gap-2 flex-wrap">
              <Button variant="ghost" onClick={handleBack} data-testid="button-back">
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
            </div>

            <div 
              ref={videoContainerRef}
              className={`${isFullscreen ? "fixed left-0 right-0 z-[100] bg-black flex flex-col safe-area-inset" : ""}`}
              style={isFullscreen ? { top: 'var(--vv-top, 0px)', height: 'calc(var(--real-vh, 1vh) * 100)' } : undefined}
            >
              <Card className={`overflow-hidden ${isFullscreen ? "flex-1 flex flex-col rounded-none border-0" : ""}`}>
                <div
                  ref={videoAreaCallbackRef}
                  className={`relative bg-black overflow-hidden ${isFullscreen ? "flex-1" : "aspect-video"}`}
                  style={{
                    transform: isMirrored ? "scaleX(-1)" : "none",
                    transition: "transform 0.3s ease",
                    cursor: zoomLevel > 1 ? (isGrabbing ? "grabbing" : "grab") : "pointer",
                    touchAction: "none",
                  }}
                  onClick={handleVideoAreaTap}
                  onPointerDown={handleVideoPanStart}
                  onPointerMove={handleVideoPanMove}
                  onPointerUp={handleVideoPanEnd}
                  onPointerCancel={handleVideoPanCancel}
                >
                  {/* Zoom wrapper — wraps only the video, not the overlays */}
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      transform: `scale(${zoomLevel}) translate(${panX / zoomLevel}px, ${panY / zoomLevel}px)`,
                      transformOrigin: "center center",
                      willChange: zoomLevel > 1 ? "transform" : "auto",
                    }}
                  >
                    {youtubeId ? (
                      <div
                        ref={ytContainerRef}
                        className="w-full h-full [&>iframe]:w-full [&>iframe]:h-full [&>div]:w-full [&>div]:h-full pointer-events-none"
                        data-testid="video-youtube"
                      />
                    ) : cloudflareStreamId ? (
                      <>
                        {cfProcessing && (
                          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-black/80" style={{ transform: isMirrored ? "scaleX(-1)" : "none" }} data-testid="video-processing-overlay">
                            <Loader2 className="w-10 h-10 animate-spin text-primary mb-4" />
                            <p className="text-white font-medium text-base mb-1">Processing your video</p>
                            <p className="text-white/60 text-sm max-w-xs text-center">This usually takes a minute or two. The video will play automatically when ready.</p>
                          </div>
                        )}
                        {!cfProcessing && cfReconnecting && (
                          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-black/80" style={{ transform: isMirrored ? "scaleX(-1)" : "none" }} data-testid="video-reconnecting-overlay">
                            <Loader2 className="w-10 h-10 animate-spin text-primary mb-4" />
                            <p className="text-white font-medium text-base mb-1">Finalizing your video</p>
                            <p className="text-white/60 text-sm max-w-xs text-center">Almost ready — this should only take a few seconds.</p>
                          </div>
                        )}
                        <CloudflareVideoPlayer
                          hlsUrl={cloudflareHlsUrl || ""}
                          playbackSpeed={playbackSpeed}
                          onVideoRef={cfVideoRefCallback}
                          onTimeUpdate={setCurrentTime}
                          onDurationChange={(dur) => {
                            setDuration(dur);
                          }}
                          onPlay={() => {
                            setIsPlaying(true);
                            trackPracticeStart("upload");
                          }}
                          onPause={() => setIsPlaying(false)}
                          onEnded={handleVideoEnded}
                          onProgress={updateBufferedPercent}
                          onWaiting={() => setIsBuffering(true)}
                          onPlaying={() => setIsBuffering(false)}
                          onRetrying={setCfReconnecting}
                          onError={(msg) => toast({ title: "Video playback error", description: msg, variant: "destructive" })}
                          className="w-full h-full object-contain pointer-events-none"
                        />
                      </>
                    ) : uploadedVideo ? (
                      <video
                        ref={videoRef}
                        src={uploadedVideo}
                        className="w-full h-full object-contain pointer-events-none"
                        preload="auto"
                        onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
                        onDurationChange={(e) => {
                          setDuration(e.currentTarget.duration);
                        }}
                        onLoadedData={(e) => {
                          if (playbackSpeed !== 1) {
                            e.currentTarget.playbackRate = playbackSpeed;
                          }
                          updateVideoContentRect();
                        }}
                        onPlay={() => {
                          setIsPlaying(true);
                          trackPracticeStart("upload");
                        }}
                        onPause={() => setIsPlaying(false)}
                        onEnded={handleVideoEnded}
                        onProgress={(e) => updateBufferedPercent(e.currentTarget)}
                        onWaiting={() => setIsBuffering(true)}
                        onPlaying={() => setIsBuffering(false)}
                        onError={(e) => {
                          const videoEl = e.currentTarget;
                          const err = videoEl.error;
                          console.error("Video load error:", err?.code, err?.message);
                          toast({
                            title: "Video failed to load",
                            description: "There was a problem loading this video. Try opening it again.",
                            variant: "destructive",
                          });
                        }}
                        playsInline
                        data-testid="video-uploaded"
                      />
                    ) : null}

                  </div>

                  {/* MIRRORED badge — outside zoom wrapper, tracks visible video bounds */}
                  {isMirrored && (
                    <div
                      className="absolute z-10 pointer-events-none"
                      style={{
                        top: visibleVideoRect ? `${visibleVideoRect.top + 8}px` : "8px",
                        left: visibleVideoRect ? `${visibleVideoRect.centerX}px` : "50%",
                        transform: "translateX(-50%) scaleX(-1)",
                      }}
                      data-testid="badge-mirrored"
                    >
                      <div className="flex items-center gap-1 bg-primary/80 text-primary-foreground text-[10px] sm:text-xs font-semibold px-2 py-0.5 rounded-full shadow-md">
                        <FlipHorizontal className="w-3 h-3" />
                        MIRRORED
                      </div>
                    </div>
                  )}

                  {/* L/R circles overlay — outside zoom wrapper, tracks visible video bounds */}
                  {showLeftRight && (
                    <div
                      className="absolute pointer-events-none flex justify-between items-start p-3 sm:p-4 z-10"
                      style={{
                        transform: isMirrored ? "scaleX(-1)" : "none",
                        top:    visibleVideoRect ? `${visibleVideoRect.top}px`    : 0,
                        left:   visibleVideoRect ? `${visibleVideoRect.left}px`   : 0,
                        right:  visibleVideoRect ? `${visibleVideoRect.right}px`  : 0,
                        bottom: visibleVideoRect ? `${visibleVideoRect.bottom}px` : 0,
                      }}
                    >
                      <div className="bg-white/90 rounded-full w-9 h-9 sm:w-11 sm:h-11 flex items-center justify-center shadow-lg">
                        <span className="text-black font-bold text-base sm:text-lg">{isMirrored ? "L" : "R"}</span>
                      </div>
                      <div className="bg-white/90 rounded-full w-9 h-9 sm:w-11 sm:h-11 flex items-center justify-center shadow-lg">
                        <span className="text-black font-bold text-base sm:text-lg">{isMirrored ? "R" : "L"}</span>
                      </div>
                    </div>
                  )}

                  {isBuffering && (uploadedVideo || cloudflareStreamId) && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10" data-testid="buffering-overlay">
                      <div
                        className="flex items-center gap-2 bg-black/60 rounded-full px-4 py-2"
                        style={{ transform: isMirrored ? "scaleX(-1)" : "none" }}
                      >
                        <Loader2 className="w-5 h-5 animate-spin text-white" />
                        <span className="text-white text-sm font-medium">
                          Buffering{bufferedPercent > 0 ? ` ${Math.round(bufferedPercent)}%` : "..."}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Skip seek indicator */}
                  {seekIndicator && (
                    <div
                      className={`absolute inset-0 flex items-center pointer-events-none z-20 ${seekIndicator.side === 'left' ? 'justify-start pl-10' : seekIndicator.side === 'right' ? 'justify-end pr-10' : 'justify-center'}`}
                      style={{ transform: isMirrored ? "scaleX(-1)" : "none" }}
                    >
                      <div
                        key={seekIndicator.id}
                        className="bg-black/60 text-white font-bold text-3xl px-5 py-2 rounded-full animate-seek-indicator"
                        onAnimationEnd={() => setSeekIndicator(null)}
                      >
                        {seekIndicator.amount > 0 ? `+${seekIndicator.amount}s` : `${seekIndicator.amount}s`}
                      </div>
                    </div>
                  )}

                  {/* Pinch-zoom level badge — outside zoom wrapper, stays fixed */}
                  <div
                    className="absolute inset-0 flex items-center justify-center pointer-events-none z-30"
                    style={{
                      opacity: showZoomBadge && zoomLevel > 1 ? 1 : 0,
                      transition: "opacity 0.3s ease",
                    }}
                  >
                    <div className="bg-black/60 text-white text-sm font-semibold px-3 py-1.5 rounded-full shadow-lg backdrop-blur-sm" style={{ transform: isMirrored ? "scaleX(-1)" : "none" }}>
                      {zoomLevel.toFixed(1)}×
                    </div>
                  </div>
                </div>

                <div
                  ref={controlsRef}
                  className={`bg-card transition-opacity duration-300 ${isFullscreen ? "bg-black/90 absolute bottom-0 left-0 right-0 z-10" : ""} ${isFullscreen && !showFullscreenControls ? "opacity-0 pointer-events-none" : ""} ${isFullscreen && showFullscreenControls ? "opacity-100 pointer-events-auto" : ""}`}
                  onClick={(e) => e.stopPropagation()}
                  onTouchStart={(e) => e.stopPropagation()}
                >
                  {/* Full-width YouTube-style timeline */}
                  <VideoTimeline
                    currentTime={currentTime}
                    duration={duration}
                    onSeek={handleSeek}
                    onSeekCommit={() => { if (isFullscreen) startHideTimer(); }}
                    isFullscreen={isFullscreen}
                    disabled={youtubeId ? !ytReady : false}
                    onPointerDown={(e) => {
                      e.stopPropagation();
                      if (isFullscreen && controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
                    }}
                  >
                    {/* Buffer progress indicator */}
                    {(uploadedVideo || cloudflareStreamId) && bufferedPercent > 0 && bufferedPercent < 100 && (
                      <div
                        className={`absolute top-1/2 -translate-y-1/2 left-0 bg-muted-foreground/20 rounded-full pointer-events-none z-0 ${isFullscreen ? "h-4" : "h-2"}`}
                        style={{ width: `${bufferedPercent}%` }}
                        data-testid="buffer-progress-bar"
                      />
                    )}
                    {/* Loop region indicator */}
                    {loopPointA !== null && loopPointB !== null && duration > 0 && (
                      <div
                        className={`absolute top-1/2 -translate-y-1/2 bg-primary/30 rounded-full pointer-events-none z-0 ${isFullscreen ? "h-4" : "h-2"}`}
                        style={{
                          left: `${(loopPointA / duration) * 100}%`,
                          width: `${((loopPointB - loopPointA) / duration) * 100}%`,
                        }}
                        data-testid="loop-region-indicator"
                      />
                    )}
                    {/* Annotation markers */}
                    {duration > 0 && annotations.map((a) => (
                      <button
                        key={a.id}
                        className="absolute top-0 bottom-0 w-0.5 bg-accent z-20 opacity-60 hover:opacity-100 cursor-pointer border-none p-0"
                        style={{ left: `calc(${(a.timestampSeconds / duration) * 100}% - 1px)` }}
                        title={`${formatTime(a.timestampSeconds)}: ${a.text}`}
                        onClick={() => handleSeek([a.timestampSeconds])}
                        data-testid={`annotation-marker-${a.id}`}
                      />
                    ))}
                  </VideoTimeline>

                  {/* Controls row */}
                  <div className="flex items-center gap-1 sm:gap-2 px-2 sm:px-4 pb-2 sm:pb-4">
                    <Button size="icon" variant="ghost" onClick={() => skipTime(-5)} data-testid="button-skip-back">
                      <ChevronLeft className="w-5 h-5" />
                    </Button>
                    <Button 
                      size="icon" 
                      onClick={togglePlay} 
                      disabled={youtubeId ? !ytReady : false}
                      data-testid="button-play-pause"
                    >
                      {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => skipTime(5)} data-testid="button-skip-forward">
                      <ChevronRight className="w-5 h-5" />
                    </Button>
                    <span className="text-xs sm:text-sm text-muted-foreground min-w-[60px] sm:min-w-[80px]" data-testid="text-time">
                      {formatTime(currentTime)} / {formatTime(duration)}
                    </span>
                    <div className="flex-1" />
                    {(uploadedVideo || youtubeId || cloudflareStreamId) && (
                      <select
                        value={playbackSpeed}
                        onChange={(e) => changeSpeed(parseFloat(e.target.value))}
                        className="bg-muted text-foreground text-xs sm:text-sm px-2 py-1 rounded border border-border focus:outline-none focus:ring-1 focus:ring-ring"
                        data-testid="select-speed"
                      >
                        {speedOptions.map((speed) => (
                          <option key={speed} value={speed}>
                            {speed}x
                          </option>
                        ))}
                      </select>
                    )}
                    <Button 
                      size="icon" 
                      variant="ghost" 
                      onClick={toggleFullscreen}
                      data-testid="button-fullscreen"
                    >
                      {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
                    </Button>
                  </div>

                  {/* Mirror, L/R & A-B Loop Controls */}
                  <div className="flex items-center justify-between gap-2 sm:gap-3 px-2 sm:px-4 pb-2 sm:pb-4 border-t border-border/50 flex-wrap pt-2">
                    <div className="flex items-center gap-3 sm:gap-4 flex-wrap">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <label htmlFor="switch-mirror" className="flex items-center gap-1.5 cursor-pointer" data-testid="toggle-mirror">
                            <FlipHorizontal className="w-4 h-4 text-muted-foreground" />
                            <span className="text-xs text-muted-foreground select-none">{isMirrored ? "Mirrored" : "Original"}</span>
                            <Switch
                              id="switch-mirror"
                              checked={isMirrored}
                              onCheckedChange={(checked) => {
                                setIsMirrored(checked);
                                posthog.capture('mirror_toggled', { enabled: checked });
                                trackEvent("practice_control_used", {
                                  surface: "home",
                                  control: "mirror",
                                  enabled: checked,
                                });
                              }}
                              aria-label="Toggle mirror mode"
                              data-testid="switch-mirror"
                            />
                          </label>
                        </TooltipTrigger>
                        <TooltipContent>{isMirrored ? "Switch to original (unmirrored) view" : "Mirror video so dancer's left = your left"}</TooltipContent>
                      </Tooltip>
                      <label htmlFor="switch-left-right" className="flex items-center gap-1.5 cursor-pointer" data-testid="toggle-left-right">
                        <span className="text-xs font-bold text-muted-foreground select-none">L / R</span>
                        <Switch
                          id="switch-left-right"
                          checked={showLeftRight}
                          onCheckedChange={(checked) => {
                            setShowLeftRight(checked);
                            posthog.capture('lr_overlay_toggled', { enabled: checked });
                            trackEvent("practice_control_used", {
                              surface: "home",
                              control: "lr_overlay",
                              enabled: checked,
                            });
                          }}
                          aria-label="Toggle L/R overlay"
                          data-testid="switch-left-right"
                        />
                      </label>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="relative">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              size="sm"
                              variant={moreControlsActive ? "secondary" : "ghost"}
                              onClick={() => setMoreOpen(true)}
                              aria-label="More controls"
                              className="px-2"
                              data-testid="button-more-controls"
                            >
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Loop, A-B loop, and share options</TooltipContent>
                        </Tooltip>
                        {moreControlsActive && (
                          <span className="absolute top-0.5 right-0.5 w-2 h-2 rounded-full bg-primary pointer-events-none" aria-hidden="true" />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* More Controls Drawer */}
                  <Drawer
                    open={moreOpen}
                    onOpenChange={(open) => {
                      setMoreOpen(open);
                      if (isFullscreen) {
                        if (open) {
                          setShowFullscreenControls(true);
                          if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
                        } else {
                          startHideTimer();
                        }
                      }
                    }}
                    shouldScaleBackground={false}
                  >
                    <DrawerContent
                      container={isFullscreen ? videoContainerRef.current : undefined}
                      className="max-h-[70vh]"
                    >
                      <DrawerHeader className="flex flex-row items-center justify-between py-3 px-4">
                        <DrawerTitle className="text-base">Controls</DrawerTitle>
                        <DrawerClose asChild>
                          <Button size="icon" variant="ghost" aria-label="Close">
                            <X className="w-4 h-4" />
                          </Button>
                        </DrawerClose>
                      </DrawerHeader>

                      <div className="overflow-y-auto pb-8 px-4 space-y-1">

                        {/* Loop */}
                        <label
                          htmlFor="drawer-switch-video-loop"
                          className={`flex items-center justify-between min-h-[52px] ${
                            loopEnabled && loopPointA !== null && loopPointB !== null
                              ? "opacity-50 pointer-events-none"
                              : "cursor-pointer"
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <Repeat1 className="w-5 h-5 text-muted-foreground" />
                            <div>
                              <div className="text-sm">Loop video</div>
                              {loopEnabled && loopPointA !== null && loopPointB !== null && (
                                <div className="text-xs text-muted-foreground">Disabled while A-B loop is active</div>
                              )}
                            </div>
                          </div>
                          <Switch
                            id="drawer-switch-video-loop"
                            checked={videoLoop}
                            disabled={loopEnabled && loopPointA !== null && loopPointB !== null}
                            onCheckedChange={(checked) => {
                              setVideoLoop(checked);
                              posthog.capture('video_loop_toggled', { enabled: checked });
                              trackEvent("practice_control_used", {
                                surface: "home",
                                control: "video_loop",
                                enabled: checked,
                              });
                            }}
                            aria-label="Toggle video loop"
                          />
                        </label>

                        <div className="border-t border-border/50" />

                        {/* A-B Loop */}
                        <div className="py-2">
                          <div className="flex items-center gap-2 mb-3">
                            <Repeat className="w-5 h-5 text-muted-foreground" />
                            <span className="text-sm font-medium">A-B Loop</span>
                          </div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <Button
                              size="default"
                              variant={loopPointA !== null ? "default" : "outline"}
                              onClick={setPointA}
                              className="flex-1 min-h-[44px]"
                              data-testid="drawer-button-set-a"
                            >
                              A {loopPointA !== null ? formatTime(loopPointA) : "—"}
                            </Button>
                            <Button
                              size="default"
                              variant={loopPointB !== null ? "default" : "outline"}
                              onClick={setPointB}
                              disabled={loopPointA === null}
                              className="flex-1 min-h-[44px]"
                              data-testid="drawer-button-set-b"
                            >
                              B {loopPointB !== null ? formatTime(loopPointB) : "—"}
                            </Button>
                            {loopPointA !== null && loopPointB !== null && (
                              <>
                                <label htmlFor="drawer-switch-ab-loop" className="flex items-center gap-2 cursor-pointer min-h-[44px]">
                                  <span className="text-sm">Active</span>
                                  <Switch
                                    id="drawer-switch-ab-loop"
                                    checked={loopEnabled}
                                    onCheckedChange={(checked) => {
                                      setLoopEnabled(checked);
                                      posthog.capture('ab_loop_toggled', { enabled: checked });
                                      trackEvent("practice_control_used", {
                                        surface: "home",
                                        control: "ab_loop",
                                        enabled: checked,
                                      });
                                    }}
                                    aria-label="Toggle A-B loop"
                                    data-testid="drawer-switch-ab-loop"
                                  />
                                </label>
                                <Button
                                  size="default"
                                  variant="ghost"
                                  onClick={clearLoop}
                                  className="min-h-[44px] px-3"
                                  data-testid="drawer-button-clear-loop"
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </div>

                        {/* Share (YouTube only) */}
                        {youtubeId && (
                          <>
                            <div className="border-t border-border/50" />
                            <div className="py-2">
                              <Button
                                variant="outline"
                                className="w-full min-h-[44px] justify-start gap-3"
                                onClick={handleShare}
                                data-testid="drawer-button-share"
                              >
                                {linkCopied ? (
                                  <Check className="w-5 h-5 text-green-500" />
                                ) : (
                                  <Share2 className="w-5 h-5" />
                                )}
                                {linkCopied ? "Link copied!" : "Share video"}
                              </Button>
                            </div>
                          </>
                        )}

                      </div>
                    </DrawerContent>
                  </Drawer>

                  {/* Save Button - centered on its own row for unsaved videos */}
                  {savedVideoId === null && (
                    <div className="flex justify-center mt-2 pt-2 border-t border-border/50">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              if (!auth.isAuthenticated || !auth.user) {
                                posthog.capture('signup_prompt_shown', { trigger: 'save_video' });
                                setUpgradeFeatureName("saving videos");
                                setUpgradeModalMode("login");
                                setUpgradeModalOpen(true);
                              } else if (uploadedFile && isOverLimit) {
                                if (!isPaid) {
                                  posthog.capture('upgrade_prompt_shown', { trigger: 'upload_limit' });
                                  setUpgradeFeatureName("saving more uploads");
                                  setUpgradeModalMode("upgrade");
                                  setUpgradeModalOpen(true);
                                } else {
                                  toast({
                                    title: "Storage limit reached",
                                    description: "You've used your 120 minutes of storage. Delete uploads from your library to free up space.",
                                    variant: "destructive",
                                  });
                                }
                              } else {
                                if (uploadedFile && isNearLimit) {
                                  toast({
                                    title: "Approaching storage limit",
                                    description: "You're nearing your 120-minute storage limit.",
                                  });
                                }
                                saveMutation.mutate();
                              }
                            }}
                            disabled={saveMutation.isPending || isSavingUpload}
                            data-testid="button-save"
                            className="text-xs px-2 sm:px-3"
                          >
                            <Bookmark className="w-4 h-4 mr-1" />
                            {isSavingUpload ? "Uploading..." : saveMutation.isPending ? "Saving..." : "Save"}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          {auth.isAuthenticated && auth.user
                            ? uploadedFile
                              ? isPaid
                                ? `Save to library (${Math.round((storageData?.usedSeconds ?? 0) / 60)}min/120min used)`
                                : `Save to library (${uploadedVideoCount}/${FREE_UPLOAD_LIMIT} upload used)`
                              : "Save to library"
                            : "Log in to save videos"}
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  )}

                  {isSavingUpload && (
                    <div className="w-full mt-2 space-y-1" data-testid="upload-progress">
                      <div className="h-2 rounded-full bg-muted overflow-hidden">
                        <div
                          className="h-full rounded-full bg-primary transition-all duration-300"
                          style={{ width: `${uploadProgress}%` }}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground text-center">
                        {uploadProgress < 10
                          ? "Preparing upload..."
                          : uploadProgress < 95
                          ? `Uploading... ${uploadProgress}%`
                          : uploadProgress < 100
                          ? "Finishing upload..."
                          : "Processing video..."}
                      </p>
                    </div>
                  )}
                  
                </div>
              </Card>
            </div>

            {!isFullscreen && savedVideoId !== null && auth.isAuthenticated && (
              <Card className="p-3 sm:p-4" data-testid="notes-section-player">
                {!isPaid ? (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Lock className="w-4 h-4 shrink-0" />
                    <span className="text-sm">Notes require a paid plan. <Link href="/pricing" className="text-primary hover:underline">Upgrade</Link></span>
                  </div>
                ) : (
                  <>
                    <button
                      className="flex items-center justify-between w-full gap-2 text-left"
                      onClick={() => {
                        if (notesOpen) {
                          if (notesSaveTimerRef.current) {
                            clearTimeout(notesSaveTimerRef.current);
                            notesSaveTimerRef.current = null;
                          }
                          if (notesDirty && savedVideoId !== null) {
                            saveNotes(savedVideoId, notesText);
                          }
                        }
                        setNotesOpen(!notesOpen);
                      }}
                      data-testid="button-toggle-notes"
                    >
                      <div className="flex items-center gap-2">
                        <StickyNote className={`w-4 h-4 ${notesText ? "text-primary" : "text-muted-foreground"}`} />
                        <span className="text-sm font-medium">Notes</span>
                        {notesSaving && <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />}
                        {!notesSaving && notesDirty && (
                          <span className="text-xs text-muted-foreground">Unsaved</span>
                        )}
                      </div>
                      {notesOpen ? (
                        <ChevronUp className="w-4 h-4 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-muted-foreground" />
                      )}
                    </button>
                    {notesOpen && (
                      <div className="mt-3 space-y-2">
                        {/* Timestamped annotations */}
                        <div className="space-y-2">
                          <span className="text-xs font-medium text-muted-foreground">Timestamped Notes</span>

                          {annotations.length > 0 && (
                            <div className="space-y-1">
                              {annotations.map((a) => (
                                <div key={a.id} className="flex items-start gap-2 group" data-testid={`annotation-row-${a.id}`}>
                                  <button
                                    className="shrink-0 text-xs font-mono bg-muted hover:bg-accent px-1.5 py-0.5 rounded text-muted-foreground hover:text-foreground transition-colors"
                                    onClick={() => handleSeek([a.timestampSeconds])}
                                    title="Seek to this moment"
                                  >
                                    {formatTime(a.timestampSeconds)}
                                  </button>
                                  {editingAnnotationId === a.id ? (
                                    <div className="flex-1 flex items-center gap-1">
                                      <Input
                                        autoFocus
                                        value={editingAnnotationText}
                                        onChange={(e) => setEditingAnnotationText(e.target.value)}
                                        maxLength={500}
                                        className="h-7 text-sm"
                                        onKeyDown={async (e) => {
                                          if (e.key === "Enter" && editingAnnotationText.trim()) {
                                            await apiRequest("PATCH", `/api/annotations/${a.id}`, { text: editingAnnotationText.trim() });
                                            setEditingAnnotationId(null);
                                            refetchAnnotations();
                                          }
                                          if (e.key === "Escape") setEditingAnnotationId(null);
                                        }}
                                      />
                                      <Button size="sm" className="h-7 px-2 text-xs shrink-0" onClick={async () => {
                                        if (editingAnnotationText.trim()) {
                                          await apiRequest("PATCH", `/api/annotations/${a.id}`, { text: editingAnnotationText.trim() });
                                          setEditingAnnotationId(null);
                                          refetchAnnotations();
                                        }
                                      }}>Save</Button>
                                      <Button size="sm" variant="ghost" className="h-7 px-2 text-xs shrink-0" onClick={() => setEditingAnnotationId(null)}>✕</Button>
                                    </div>
                                  ) : (
                                    <>
                                      <span className="flex-1 text-sm leading-snug py-0.5">{a.text}</span>
                                      <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                                        <Button
                                          size="icon"
                                          variant="ghost"
                                          className="h-6 w-6"
                                          onClick={() => { setEditingAnnotationId(a.id); setEditingAnnotationText(a.text); }}
                                          title="Edit"
                                        >
                                          <Pencil className="w-3 h-3" />
                                        </Button>
                                        <Button
                                          size="icon"
                                          variant="ghost"
                                          className="h-6 w-6 text-destructive hover:text-destructive"
                                          onClick={async () => {
                                            await apiRequest("DELETE", `/api/annotations/${a.id}`);
                                            refetchAnnotations();
                                          }}
                                          title="Delete"
                                        >
                                          <Trash2 className="w-3 h-3" />
                                        </Button>
                                      </div>
                                    </>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}

                          {pendingAnnotationTime !== null ? (
                            <div className="flex items-center gap-1.5">
                              <span className="shrink-0 text-xs font-mono bg-muted px-1.5 py-0.5 rounded text-muted-foreground">
                                {formatTime(pendingAnnotationTime)}
                              </span>
                              <Input
                                autoFocus
                                placeholder="Add a note..."
                                maxLength={500}
                                value={newAnnotationText}
                                onChange={(e) => setNewAnnotationText(e.target.value)}
                                className="h-7 text-sm"
                                onKeyDown={async (e) => {
                                  if (e.key === "Enter" && newAnnotationText.trim() && savedVideoId !== null) {
                                    await apiRequest("POST", `/api/videos/${savedVideoId}/annotations`, {
                                      timestampSeconds: pendingAnnotationTime,
                                      text: newAnnotationText.trim(),
                                    });
                                    setNewAnnotationText("");
                                    setPendingAnnotationTime(null);
                                    refetchAnnotations();
                                  }
                                  if (e.key === "Escape") { setPendingAnnotationTime(null); setNewAnnotationText(""); }
                                }}
                              />
                              <Button size="sm" className="h-7 px-2 text-xs shrink-0" onClick={async () => {
                                if (newAnnotationText.trim() && savedVideoId !== null) {
                                  await apiRequest("POST", `/api/videos/${savedVideoId}/annotations`, {
                                    timestampSeconds: pendingAnnotationTime,
                                    text: newAnnotationText.trim(),
                                  });
                                  setNewAnnotationText("");
                                  setPendingAnnotationTime(null);
                                  refetchAnnotations();
                                }
                              }}>Save</Button>
                              <Button size="sm" variant="ghost" className="h-7 px-2 text-xs shrink-0" onClick={() => { setPendingAnnotationTime(null); setNewAnnotationText(""); }}>✕</Button>
                            </div>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs gap-1 w-full"
                              onClick={() => setPendingAnnotationTime(Math.round(currentTime))}
                              data-testid="button-add-annotation"
                            >
                              <Plus className="w-3 h-3" />
                              Note at {formatTime(Math.round(currentTime))}
                            </Button>
                          )}
                        </div>

                        {/* General notes */}
                        <div className="border-t border-border pt-3 space-y-2">
                          <Textarea
                            value={notesText}
                            onChange={(e) => handleNotesChange(savedVideoId, e.target.value)}
                            placeholder="Add counts, cues, choreography tips..."
                            className="text-sm resize-none min-h-[100px]"
                            maxLength={5000}
                            data-testid="textarea-notes-player"
                          />
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-xs text-muted-foreground">{notesText.length}/5000</span>
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={!notesDirty || notesSaving}
                              onClick={() => {
                                if (notesSaveTimerRef.current) {
                                  clearTimeout(notesSaveTimerRef.current);
                                  notesSaveTimerRef.current = null;
                                }
                                if (savedVideoId !== null) {
                                  saveNotes(savedVideoId, notesText);
                                }
                              }}
                              data-testid="button-save-notes-player"
                            >
                              {notesSaving ? "Saving..." : "Save"}
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </Card>
            )}

            {!isFullscreen && (
              <Card className="p-3 sm:p-4 hidden sm:block">
                <h3 className="font-medium mb-2 sm:mb-3 text-sm sm:text-base">Quick Tips</h3>
                <ul className="text-xs sm:text-sm text-muted-foreground space-y-2">
                  <li className="flex items-start gap-2">
                    <FlipHorizontal className="w-3 h-3 sm:w-4 sm:h-4 mt-0.5 flex-shrink-0" />
                    <span><strong>Mirror Mode:</strong> When you face the dancer, their left becomes your right. Mirror mode fixes this so you can copy their movements directly.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-bold text-xs sm:text-sm mt-0.5 flex-shrink-0">L R</span>
                    <span><strong>L/R Overlay:</strong> Shows left and right markers. When mirrored, shows L/R from your perspective. When not mirrored, shows the dancer's L/R so you know which side is which.</span>
                  </li>
                  {(youtubeId || uploadedVideo || cloudflareStreamId) ? (
                    <>
                      <li className="flex items-start gap-2">
                        <Gauge className="w-3 h-3 sm:w-4 sm:h-4 mt-0.5 flex-shrink-0" />
                        <span><strong>Speed Control:</strong> Slow down to 0.25x to learn tricky moves, or speed up to 2x for a challenge.</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Repeat className="w-3 h-3 sm:w-4 sm:h-4 mt-0.5 flex-shrink-0" />
                        <span><strong>A-B Looping:</strong> Set start and end points to loop a specific section for practice.</span>
                      </li>
                    </>
                  ) : null}
                </ul>
              </Card>
            )}
          </div>
        )}
      </main>

      <UpgradeModal
        open={upgradeModalOpen}
        onOpenChange={setUpgradeModalOpen}
        mode={upgradeModalMode}
        featureName={upgradeFeatureName}
      />

      <AppFooter />
    </div>
  );
}
